import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useQuran } from '../context/QuranContext';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, ChevronRight, Save, Edit3, X, Check, Sparkles, Loader2, MessageSquare, Copy, Trash2, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Skeleton } from './ui/skeleton';
import WordByWord from './WordByWord';
import LoginPromptDialog from './LoginPromptDialog';
import AddToTopic from './AddToTopic';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerseDisplay = () => {
    const {
        currentSurah,
        currentVerse,
        verseData,
        loading,
        error,
        goToNextVerse,
        goToPreviousVerse,
        customTranslations,
        saveCustomTranslation,
        deleteCustomTranslation,
        surahs
    } = useQuran();

    const { isAuthenticated, getAuthHeaders } = useAuth();
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [loginPromptFeature, setLoginPromptFeature] = useState('');

    const [isEditingCustom, setIsEditingCustom] = useState(false);
    const [customText, setCustomText] = useState('');
    const [customReasoning, setCustomReasoning] = useState('');

    // AI Translation states
    const [aiTranslation, setAiTranslation] = useState('');
    const [aiReasoning, setAiReasoning] = useState('');
    const [verseThoughts, setVerseThoughts] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [customMeaningsForVerse, setCustomMeaningsForVerse] = useState([]);
    const [rootBasedMeaningsInfo, setRootBasedMeaningsInfo] = useState([]);

    const verseKey = currentSurah ? `${currentSurah.id}:${currentVerse}` : '';
    const existingCustomObj = customTranslations[verseKey] || null;
    const existingCustom = typeof existingCustomObj === 'string' ? existingCustomObj : (existingCustomObj?.text || '');
    const existingReasoning = existingCustomObj?.reasoning || '';

    // Helper to check auth before protected action
    const requireAuth = (featureName) => {
        if (!isAuthenticated) {
            setLoginPromptFeature(featureName);
            setShowLoginPrompt(true);
            return false;
        }
        return true;
    };

    // Load custom meanings for current verse words (including root-based meanings)
    useEffect(() => {
        if (!verseData?.words) return;

        const loadCustomMeanings = async () => {
            const promises = verseData.words.map(async (word) => {
                if (word.char_type_name === 'end') return null;

                // Construct word location (surah:verse:position)
                const wordLocation = word.location || null;

                let validMeaningObj = null;
                let rootMeaningInfoObj = null;

                try {
                    // Get direct custom meanings for this word (requires auth)
                    let customMeaningsList = [];
                    if (isAuthenticated) {
                        try {
                            const response = await axios.get(`${API}/custom-meanings/${word.id}`, {
                                headers: getAuthHeaders()
                            });
                            customMeaningsList = response.data.meanings || [];
                        } catch (e) {
                            console.warn('Could not fetch direct meanings:', e);
                        }
                    }

                    // Include ALL custom meanings for this word
                    const allCustomMeanings = customMeaningsList.map(m => ({
                        meaning: m.meaning,
                        reasoning: m.reasoning,
                        root: m.root,
                        source: 'direct' // meaning defined for this word directly
                    }));

                    // Now fetch the root for this word and get root-based meanings
                    let rootBasedMeanings = [];
                    let wordRoot = null;

                    if (wordLocation && wordLocation.split(':').length >= 3) {
                        try {
                            // Get the word's root
                            const morphResponse = await axios.get(`${API}/morphology/word/${wordLocation}`);
                            const rootTags = morphResponse.data?.data?.morphology?.tags_by_category?.Root || [];
                            wordRoot = rootTags[0]?.code;

                            if (wordRoot && isAuthenticated) {
                                // Get all custom meanings for this root
                                const rootResponse = await axios.get(`${API}/custom-meanings/by-root/${encodeURIComponent(wordRoot)}`, {
                                    headers: getAuthHeaders()
                                });
                                const allRootMeanings = rootResponse.data.meanings || [];

                                // Filter to meanings NOT from this specific word
                                rootBasedMeanings = allRootMeanings
                                    .filter(m => m.root_id !== word.id)
                                    .map(m => ({
                                        meaning: m.meaning,
                                        reasoning: m.reasoning,
                                        root: m.root,
                                        source: 'root', // meaning from another word with same root
                                        source_word: m.root_text,
                                        source_location: m.word_location
                                    }));

                                if (rootBasedMeanings.length > 0) {
                                    rootMeaningInfoObj = {
                                        word: word.text_uthmani || word.text,
                                        root: wordRoot,
                                        count: rootBasedMeanings.length,
                                        sources: rootBasedMeanings.map(m => m.source_word)
                                    };
                                }
                            }
                        } catch (err) {
                            console.warn('Could not fetch root-based meanings:', err);
                        }
                    }

                    // Combine direct and root-based meanings
                    const combinedMeanings = [...allCustomMeanings, ...rootBasedMeanings];

                    validMeaningObj = {
                        word: word.text_uthmani || word.text,
                        word_id: word.id,
                        word_location: wordLocation,
                        word_root: wordRoot,
                        transliteration: word.transliteration?.text || '',
                        standard_meaning: word.translation?.text || '',
                        custom_meanings: combinedMeanings,
                        has_root_meanings: rootBasedMeanings.length > 0,
                        // For backward compatibility
                        custom_meaning: combinedMeanings.length > 0 ? combinedMeanings.map(m => m.meaning).join('; ') : null,
                        custom_reasoning: combinedMeanings.length > 0 ? combinedMeanings.map(m => m.reasoning).filter(Boolean).join('; ') : null
                    };
                } catch (err) {
                    validMeaningObj = {
                        word: word.text_uthmani || word.text,
                        word_id: word.id,
                        word_location: wordLocation,
                        word_root: null,
                        transliteration: word.transliteration?.text || '',
                        standard_meaning: word.translation?.text || '',
                        custom_meanings: [],
                        has_root_meanings: false,
                        custom_meaning: null,
                        custom_reasoning: null
                    };
                }

                return { meaningObj: validMeaningObj, rootMeaningObj: rootMeaningInfoObj };
            });

            const results = await Promise.all(promises);

            const meanings = [];
            const rootMeaningsInfo = [];

            for (const res of results) {
                if (!res) continue;
                if (res.meaningObj) meanings.push(res.meaningObj);
                if (res.rootMeaningObj) rootMeaningsInfo.push(res.rootMeaningObj);
            }

            setCustomMeaningsForVerse(meanings);
            setRootBasedMeaningsInfo(rootMeaningsInfo);
        };

        loadCustomMeanings();
    }, [verseData?.words, isAuthenticated, getAuthHeaders]);

    // Clear AI translation when verse changes
    useEffect(() => {
        setAiTranslation('');
        setAiReasoning('');
        setVerseThoughts('');
        setRootBasedMeaningsInfo([]);
        setCustomMeaningsForVerse([]);
    }, [verseKey]);

    const handleEditCustom = () => {
        if (!requireAuth('Custom Translations')) return;
        setCustomText(existingCustom);
        setCustomReasoning(existingReasoning);
        setIsEditingCustom(true);
    };

    const handleSaveCustom = async () => {
        const success = await saveCustomTranslation(verseKey, customText, customReasoning);
        if (success) {
            toast.success('Custom translation saved');
            setIsEditingCustom(false);
        } else {
            toast.error('Failed to save translation');
        }
    };

    const handleDeleteCustom = async () => {
        const success = await deleteCustomTranslation(verseKey);
        if (success) {
            toast.success('Custom translation deleted');
        } else {
            toast.error('Failed to delete translation');
        }
    };

    const handleCancelEdit = () => {
        setCustomText('');
        setCustomReasoning('');
        setIsEditingCustom(false);
    };

    const handleGenerateAI = async () => {
        if (!verseData) return;
        if (!requireAuth('AI Translation')) return;

        setIsGenerating(true);
        setAiTranslation('');
        setAiReasoning('');

        try {
            const response = await axios.post(`${API}/ai-translate`, {
                verse_key: verseKey,
                arabic_text: verseData.text_uthmani || verseData.text_indopak,
                word_meanings: customMeaningsForVerse,
                standard_translation: verseData.translations?.[0]?.text || '',
                verse_thoughts: verseThoughts || null
            }, {
                headers: getAuthHeaders()
            });

            setAiTranslation(response.data.translation);
            setAiReasoning(response.data.reasoning);
            toast.success('AI translation generated');
        } catch (err) {
            console.error('Failed to generate AI translation:', err);
            toast.error('Failed to generate AI translation');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSaveAIAsCustom = async () => {
        if (!aiTranslation) return;

        const success = await saveCustomTranslation(verseKey, aiTranslation, aiReasoning);
        if (success) {
            toast.success('AI translation saved as custom translation');
        } else {
            toast.error('Failed to save translation');
        }
    };

    const handleCopyAI = () => {
        try {
            const copyText = verseKey ? `[${verseKey}] ${aiTranslation}` : aiTranslation;
            navigator.clipboard.writeText(copyText);
            toast.success('AI translation copied to clipboard');
        } catch (err) {
            console.error('Failed to copy AI translation:', err);
            toast.error('Failed to copy to clipboard');
        }
    };

    const handleShare = async () => {
        const shareTitle = `Ai Quran: ${currentSurah?.name_simple} ${currentVerse}`;
        const shareText = `Explore Surah ${currentSurah?.name_simple}, Verse ${currentVerse} on Ai Quran.`;
        const shareUrl = `${window.location.origin}/?surah=${currentSurah?.id}&verse=${currentVerse}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: shareUrl,
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    toast.error('Error sharing');
                }
            }
        } else {
            // Fallback to copying the link
            try {
                await navigator.clipboard.writeText(shareUrl);
                toast.success('Link copied to clipboard');
            } catch (err) {
                toast.error('Failed to copy link');
            }
        }
    };

    // Navigation checks
    const isFirstVerse = currentSurah?.id === 1 && currentVerse === 1;
    const isLastVerse = currentSurah?.id === 114 && currentVerse === currentSurah?.verses_count;

    // Keyboard navigation
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;

            if (e.key === 'ArrowRight' && !isLastVerse) {
                goToNextVerse();
            } else if (e.key === 'ArrowLeft' && !isFirstVerse) {
                goToPreviousVerse();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [goToNextVerse, goToPreviousVerse, isFirstVerse, isLastVerse]);

    // Count total custom meanings across all words
    const customMeaningsCount = customMeaningsForVerse.reduce((total, m) =>
        total + (m.custom_meanings?.length || (m.custom_meaning ? 1 : 0)), 0
    );

    // Count direct vs root-based meanings
    const directMeaningsCount = customMeaningsForVerse.reduce((total, m) =>
        total + (m.custom_meanings?.filter(cm => cm.source === 'direct').length || 0), 0
    );

    const uniqueRootMeanings = new Set();
    customMeaningsForVerse.forEach(m => {
        m.custom_meanings?.forEach(cm => {
            if (cm.source === 'root') {
                uniqueRootMeanings.add(`${cm.root}-${cm.meaning}-${cm.source_location}`);
            }
        });
    });
    const rootMeaningsCount = uniqueRootMeanings.size;

    // Get words that have any custom meanings
    const wordsWithMeanings = customMeaningsForVerse.filter(m => m.custom_meanings?.length > 0);

    // Copy to clipboard helper - includes verse reference
    const copyToClipboard = async (text, label, includeVerseRef = true) => {
        try {
            let copyText = text;
            if (includeVerseRef && verseKey) {
                copyText = `[${verseKey}] ${text}`;
            }
            await navigator.clipboard.writeText(copyText);
            toast.success(`${label} copied to clipboard`);
        } catch (err) {
            console.error('Failed to copy:', err);
            toast.error('Failed to copy to clipboard');
        }
    };

    if (loading) {
        return (
            <div className="verse-area fade-in" data-testid="verse-loading">
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48 mx-auto bg-[#1e293b]" />
                    <Skeleton className="h-24 w-full bg-[#1e293b]" />
                    <Skeleton className="h-16 w-full bg-[#1e293b]" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="verse-area" data-testid="verse-error">
                <p className="text-center text-[#ef4444]">{error}</p>
            </div>
        );
    }

    if (!verseData || !currentSurah) {
        return (
            <div className="verse-area" data-testid="verse-empty">
                <p className="text-center text-[#d4af37] text-lg font-medium">Ai Quran is activating, please wait for 30 seconds.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 fade-in" data-testid="verse-display">
            {/* Verse Header */}
            <div className="text-center mb-4">
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                    <span className="badge-gold ui-text" data-testid="verse-badge">
                        {currentSurah.name_simple} ({currentSurah.name_arabic})
                    </span>
                    <h2 className="heading-primary text-xl md:text-2xl text-[#f8fafc] mb-0" data-testid="verse-key">
                        Verse {currentVerse} of {currentSurah.verses_count}
                    </h2>
                </div>
            </div>

            {/* Arabic Text - Main Verse Display */}
            <div className="verse-container verse-area relative" data-testid="arabic-verse-container">
                <div className="absolute top-2 right-2 flex gap-1 z-10">
                    <AddToTopic
                        verseKey={verseKey}
                        surahName={currentSurah?.name_simple}
                    />
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleShare}
                        className="text-[#64748b] hover:text-[#d4af37] hover:bg-[#d4af37]/10"
                        title="Share verse"
                        data-testid="share-verse-btn"
                    >
                        <Share2 className="w-4 h-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(verseData.text_uthmani || verseData.text_indopak, 'Arabic verse')}
                        className="text-[#64748b] hover:text-[#d4af37] hover:bg-[#d4af37]/10"
                        data-testid="copy-arabic-btn"
                    >
                        <Copy className="w-4 h-4" />
                    </Button>
                </div>
                <p
                    className="arabic-text text-2xl md:text-3xl lg:text-4xl !leading-[3rem] md:!leading-[3.7rem] lg:!leading-[4.5rem] text-[#f8fafc] pt-6 lg:pt-2 pb-0 -mb-2 lg:-mb-6"
                    dir="rtl"
                    lang="ar"
                    aria-label="Arabic verse text"
                    data-testid="arabic-verse-text"
                >
                    {verseData.text_uthmani || verseData.text_indopak}
                </p>
            </div>

            {/* Translation Tabs */}
            <Tabs defaultValue="translation" className="w-full">
                <TabsList className="w-full bg-[#09090b] border border-[#1e293b] p-1">
                    <TabsTrigger
                        value="translation"
                        className="flex-1 data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-sm"
                        data-testid="tab-translation"
                    >
                        Translation
                    </TabsTrigger>
                    <TabsTrigger
                        value="custom"
                        className="flex-1 data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-sm"
                        data-testid="tab-custom"
                    >
                        <Sparkles className="w-4 h-4 mr-1 sm:mr-2" />
                        AI / Custom
                    </TabsTrigger>
                    <TabsTrigger
                        value="word-by-word"
                        className="flex-1 data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-sm"
                        data-testid="tab-word-by-word"
                    >
                        Word by Word
                    </TabsTrigger>
                </TabsList>

                {/* Standard Translation */}
                <TabsContent value="translation" className="mt-4">
                    <div className="translation-panel relative" data-testid="translation-panel">
                        {verseData.translations?.length > 0 ? (
                            <div className="space-y-4">
                                {verseData.translations.map((trans, idx) => (
                                    <div key={idx} className="pb-4 mb-4 last:pb-0 last:mb-0 border-b last:border-0 border-[#1e293b]/50">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => copyToClipboard(
                                                trans.text.replace(/<[^>]*>/g, ''),
                                                verseData.translations.length > 1 ? trans.resource_name : 'Translation'
                                            )}
                                            className="float-right text-[#64748b] hover:text-[#f8fafc] hover:bg-[#1e293b] h-8 w-8 p-0 ml-3 mb-1"
                                            data-testid={`copy-translation-btn-${idx}`}
                                            title="Copy translation"
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                        {verseData.translations.length > 1 && (
                                            <p className="text-sm text-[#d4af37] opacity-80 font-medium mb-2 pt-1">
                                                {trans.resource_name}
                                            </p>
                                        )}
                                        <p
                                            className="text-lg md:text-xl text-[#f8fafc] leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: trans.text }}
                                            data-testid={`translation-text-${idx}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[#94a3b8] text-center">No translation available</p>
                        )}
                    </div>
                </TabsContent>

                {/* AI / Custom Translation */}
                <TabsContent value="custom" className="mt-4">
                    <div className="space-y-4">
                        {/* AI Generation Section */}
                        <div className="translation-panel space-y-4">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-[#d4af37]" />
                                    <h3 className="text-[#f8fafc] font-medium">AI Translation Generator</h3>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {directMeaningsCount > 0 && (
                                        <span className="text-xs text-[#d4af37] bg-[#d4af37]/10 px-2 py-1 rounded">
                                            {directMeaningsCount} direct meaning{directMeaningsCount > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {rootMeaningsCount > 0 && (
                                        <span className="text-xs text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded" data-testid="root-meanings-indicator">
                                            +{rootMeaningsCount} from same roots
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Show which words have available meanings */}
                            {wordsWithMeanings.length > 0 && (
                                <div className="p-3 bg-[#d4af37]/10 rounded-lg border border-[#d4af37]/30" data-testid="ai-words-detail">
                                    <p className="text-xs text-[#d4af37] font-medium mb-2">
                                        Words available for AI translation:
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {wordsWithMeanings.map((w, idx) => {
                                            const isDirect = w.custom_meanings?.some(cm => cm.source === 'direct');
                                            const rootColor = isDirect ? 'text-[#d4af37]' : 'text-[#10b981]';
                                            const borderColor = isDirect ? 'border-[#d4af37]/50' : 'border-[#10b981]/50';
                                            return (
                                                <span key={idx} className={`text-xs text-[#f8fafc] bg-[#1e293b] border ${borderColor} px-2 py-1 rounded`}>
                                                    <span className="arabic-text" dir="rtl">{w.word}</span>
                                                    {w.word_root && (
                                                        <span className={`${rootColor} opacity-80 ml-1`}>(root: {w.word_root})</span>
                                                    )}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Verse-specific thoughts input */}
                            <div className="space-y-2">
                                <label className="text-xs uppercase tracking-[0.2em] text-[#94a3b8] ui-text flex items-center gap-2">
                                    <MessageSquare className="w-3 h-3" />
                                    Verse Thoughts (optional)
                                </label>
                                <Textarea
                                    value={verseThoughts}
                                    onChange={(e) => setVerseThoughts(e.target.value)}
                                    placeholder="Add any specific thoughts or context for this verse to guide the AI..."
                                    className="bg-[#0f172a]/50 border-[#1e293b] text-[#f8fafc] min-h-[80px] text-sm"
                                    data-testid="verse-thoughts-input"
                                />
                            </div>

                            {/* Generate Button */}
                            <Button
                                onClick={handleGenerateAI}
                                disabled={isGenerating}
                                className="w-full bg-gradient-to-r from-[#d4af37] to-[#a16207] text-[#02040a] font-semibold hover:from-[#facc15] hover:to-[#d4af37] h-12"
                                data-testid="generate-ai-btn"
                            >
                                {isGenerating ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Generating Translation...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-5 h-5 mr-2" />
                                        Generate AI Translation
                                    </>
                                )}
                            </Button>

                            {/* AI Translation Result */}
                            {aiTranslation && (
                                <div className="space-y-4 pt-4 border-t border-[#1e293b]">
                                    <div className="p-4 bg-[#0f172a]/50 rounded-lg border border-[#d4af37]/30">
                                        <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-3 ui-text">
                                            AI Generated Translation
                                        </p>
                                        <p className="text-lg text-[#f8fafc] leading-relaxed whitespace-pre-wrap" data-testid="ai-translation-result">
                                            {aiTranslation}
                                        </p>
                                    </div>

                                    {aiReasoning && (
                                        <div className="p-4 bg-[#0f172a]/30 rounded-lg border border-[#1e293b]">
                                            <p className="text-xs uppercase tracking-[0.2em] text-[#94a3b8] mb-2 ui-text">
                                                AI Reasoning
                                            </p>
                                            <p className="text-sm text-[#94a3b8] leading-relaxed whitespace-pre-wrap" data-testid="ai-reasoning">
                                                {aiReasoning}
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <Button
                                            onClick={handleCopyAI}
                                            variant="outline"
                                            className="flex-1 border-[#1e293b] text-[#94a3b8] hover:text-[#f8fafc]"
                                            data-testid="copy-ai-btn"
                                        >
                                            <Copy className="w-4 h-4 mr-2" />
                                            Copy
                                        </Button>
                                        <Button
                                            onClick={handleSaveAIAsCustom}
                                            className="flex-1 bg-[#10b981] hover:bg-[#059669] text-white font-semibold"
                                            data-testid="save-ai-as-custom-btn"
                                        >
                                            <Save className="w-4 h-4 mr-2" />
                                            Save as Custom
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Saved Custom Translation */}
                        <div className="translation-panel" data-testid="custom-translation-panel">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-[#94a3b8] text-xs uppercase tracking-[0.2em] ui-text">
                                    Saved Custom Translation
                                </h3>
                                {!existingCustom && !isEditingCustom && (
                                    <Button
                                        onClick={handleEditCustom}
                                        variant="ghost"
                                        size="sm"
                                        className="border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10"
                                        data-testid="add-custom-btn"
                                    >
                                        <Edit3 className="w-4 h-4 mr-1" />
                                        Add
                                    </Button>
                                )}
                            </div>

                            {isEditingCustom ? (
                                <div className="space-y-4">
                                    <Textarea
                                        value={customText}
                                        onChange={(e) => setCustomText(e.target.value)}
                                        placeholder="Enter your custom translation..."
                                        className="custom-textarea"
                                        rows={4}
                                        data-testid="custom-translation-textarea"
                                    />
                                    <Textarea
                                        value={customReasoning}
                                        onChange={(e) => setCustomReasoning(e.target.value)}
                                        placeholder="Reasoning (optional)"
                                        className="bg-[#0f172a] border-[#1e293b] text-[#f8fafc] min-h-[60px]"
                                        rows={2}
                                        data-testid="custom-translation-reasoning-textarea"
                                    />
                                    <div className="flex gap-3 justify-end">
                                        <Button
                                            variant="outline"
                                            onClick={handleCancelEdit}
                                            className="border-[#1e293b] text-[#94a3b8] hover:bg-[#1e293b]"
                                            data-testid="cancel-custom-btn"
                                        >
                                            <X className="w-4 h-4 mr-2" />
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleSaveCustom}
                                            className="btn-gold"
                                            data-testid="save-custom-btn"
                                        >
                                            <Check className="w-4 h-4 mr-2" />
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                existingCustom ? (
                                    <div className="block p-4 bg-[#0f172a]/50 rounded-lg border border-[#d4af37]/30">
                                        <div className="float-right flex gap-1 ml-3 mb-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => copyToClipboard(
                                                    existingCustom + (existingReasoning ? `\nReasoning: ${existingReasoning}` : ''),
                                                    'Custom translation'
                                                )}
                                                className="text-[#64748b] hover:text-[#f8fafc] hover:bg-[#1e293b] h-8 w-8 p-0"
                                                data-testid="copy-custom-translation-btn"
                                            >
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={handleEditCustom}
                                                className="text-[#d4af37] hover:text-[#facc15] hover:bg-[#d4af37]/10 h-8 w-8 p-0"
                                                data-testid="edit-custom-translation-btn"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={handleDeleteCustom}
                                                className="text-[#ef4444] hover:text-[#ef4444] hover:bg-[#ef4444]/10 h-8 w-8 p-0"
                                                data-testid="delete-custom-translation-btn"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                        <div className="clear-left">
                                            <p
                                                className="text-lg text-[#f8fafc] leading-relaxed mb-2 whitespace-pre-wrap"
                                                data-testid="custom-translation-display"
                                            >
                                                {existingCustom}
                                            </p>
                                            {existingReasoning && (
                                                <p className="text-[#94a3b8] text-sm italic whitespace-pre-wrap clear-both mt-2">
                                                    Reasoning: {existingReasoning}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-[#94a3b8] text-center italic py-4">
                                        No custom translation saved yet
                                    </p>
                                )
                            )}
                        </div>
                    </div>
                </TabsContent>

                {/* Word by Word */}
                <TabsContent value="word-by-word" className="mt-4">
                    <WordByWord words={verseData.words || []} />
                </TabsContent>
            </Tabs>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 border-t border-[#1e293b]">
                <Button
                    onClick={goToPreviousVerse}
                    disabled={isFirstVerse}
                    className="nav-button"
                    data-testid="prev-verse-btn"
                >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="hidden sm:inline">Previous Verse</span>
                </Button>

                <div className="text-center">
                    <span className="text-[#94a3b8] ui-text text-sm">
                        Use <kbd className="px-2 py-1 bg-[#1e293b] rounded text-xs mx-1">←</kbd>
                        <kbd className="px-2 py-1 bg-[#1e293b] rounded text-xs mx-1">→</kbd>
                        to navigate
                    </span>
                </div>

                <Button
                    onClick={goToNextVerse}
                    disabled={isLastVerse}
                    className="nav-button"
                    data-testid="next-verse-btn"
                >
                    <span className="hidden sm:inline">Next Verse</span>
                    <ChevronRight className="w-5 h-5" />
                </Button>
            </div>

            {/* Login Prompt Dialog */}
            <LoginPromptDialog
                isOpen={showLoginPrompt}
                onClose={() => setShowLoginPrompt(false)}
                featureName={loginPromptFeature}
            />
        </div>
    );
};

export default VerseDisplay;
